import { useState, useEffect } from "react";
import {
  Stethoscope,
  LogOut,
  Shield,
  UserCheck,
  Activity,
  ChevronRight,
  Sparkles,
  ClipboardList,
} from "lucide-react";
import Login from "./components/Login";
import { IQDoctorLogo } from "./components/IQDoctorLogo";
import PhysicianDashboard from "./components/PhysicianDashboard";
import NursingDashboard from "./components/NursingDashboard";
import LabXrayDashboard from "./components/LabXrayDashboard";
import ErrorBoundary from "./components/ErrorBoundary";
import PharmacyDashboard from "./components/PharmacyDashboard";
import ReceptionDashboard from "./components/ReceptionDashboard";
import SuperAdminDashboard from "./components/SuperAdminDashboard";

import PublicPortal from "./components/PublicPortal";
import QueueDisplay from "./components/QueueDisplay";
import DrWathiqBookingPage from "./components/DrWathiqBookingPage";
import GoogleSheetBackgroundSync from "./components/GoogleSheetBackgroundSync";
import MedicalCalculators from "./components/MedicalCalculators";
import PatientLinkGate from "./components/PatientLinkGate";
import StickerMakerLanding from "./components/StickerMakerLanding";
import StickerMakerLandingEn from "./components/StickerMakerLandingEn";

import { PatientProvider } from "./context/PatientContext";
import PatientBanner from "./components/PatientBanner";

// --- START OF OFFLINE-FIRST & CACHING INTERCEPTOR ---
const originalFetch = window.fetch;

// Endpoints to cache for blazing fast performance (Stale-While-Revalidate)
const CACHEABLE_ENDPOINTS = [
  "/api/leaflet-templates",
  "/api/admin/users",
  "/api/clinic/profile"
];

const getOfflineQueue = () => {
  try {
    return JSON.parse(localStorage.getItem("iq_doctor_offline_queue") || "[]");
  } catch {
    return [];
  }
};

const setOfflineQueue = (queue: any[]) => {
  localStorage.setItem("iq_doctor_offline_queue", JSON.stringify(queue));
};

const processOfflineQueue = async () => {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;
  
  const notify = (msg: string, isError = false) => {
    const el = document.createElement("div");
    el.style.position = "fixed";
    el.style.bottom = "20px";
    el.style.right = "20px";
    el.style.backgroundColor = isError ? "#EF4444" : "#10B981";
    el.style.color = "white";
    el.style.padding = "12px 24px";
    el.style.borderRadius = "8px";
    el.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
    el.style.zIndex = "9999";
    el.style.fontWeight = "bold";
    el.style.fontFamily = "sans-serif";
    el.dir = "rtl";
    el.innerText = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  };

  notify(`جاري مزامنة ${queue.length} طلبات تم حفظها أثناء انقطاع الإنترنت...`);
  
  let successCount = 0;
  const remainingQueue = [];

  for (const item of queue) {
    try {
      const res = await originalFetch(item.url, item.options);
      if (res.ok) {
        successCount++;
      } else {
        remainingQueue.push(item);
      }
    } catch (err) {
      remainingQueue.push(item);
    }
  }

  setOfflineQueue(remainingQueue);
  if (successCount > 0) {
    notify(`تمت مزامنة ${successCount} طلبات بنجاح!`);
    window.dispatchEvent(new Event("databaseSynchronised"));
  }
};

window.addEventListener("online", processOfflineQueue);
// Auto-sync on app startup (in case browser was closed and reopened with internet)
setTimeout(processOfflineQueue, 2000);

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const urlString = typeof input === "string" ? input : input.toString();
  
  const isRead = !init || !init.method || init.method.toUpperCase() === "GET";
  const isWrite = init && ["POST", "PUT", "DELETE", "PATCH"].includes(init.method?.toUpperCase() || "");
  
  // 1. FAST CACHING LOGIC (Stale-While-Revalidate)
  if (isRead && CACHEABLE_ENDPOINTS.some(ep => urlString.includes(ep))) {
    const cacheKey = `iq_cache_${urlString}`;
    
    const fetchPromise = originalFetch(input, init)
      .then(async (res) => {
        const clonedRes = res.clone();
        if (clonedRes.ok) {
          const text = await clonedRes.text();
          localStorage.setItem(cacheKey, text);
        }
        return res;
      })
      .catch(err => {
        console.warn("Background fetch failed, keeping cache.", err);
        throw err;
      });

    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      return new Response(cachedData, {
        status: 200,
        headers: { "Content-Type": "application/json" }
      });
    }
    
    return fetchPromise;
  }

  // 2. OFFLINE-FIRST WRITE LOGIC
  try {
    return await originalFetch(input, init);
  } catch (error: any) {
    if (isWrite && (error.message === "Failed to fetch" || error.message.includes("NetworkError"))) {
      
      const notify = () => {
        const el = document.createElement("div");
        el.style.position = "fixed";
        el.style.bottom = "20px";
        el.style.right = "20px";
        el.style.backgroundColor = "#F59E0B";
        el.style.color = "white";
        el.style.padding = "12px 24px";
        el.style.borderRadius = "8px";
        el.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
        el.style.zIndex = "9999";
        el.style.fontWeight = "bold";
        el.style.fontFamily = "sans-serif";
        el.dir = "rtl";
        el.innerText = "أنت غير متصل بالإنترنت. تم حفظ التعديل وسيتم رفعه تلقائياً عند عودة الاتصال.";
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 5000);
      };
      
      notify();

      const queue = getOfflineQueue();
      queue.push({
        url: urlString,
        options: {
          method: init.method,
          headers: init.headers,
          body: init.body,
        },
        timestamp: Date.now()
      });
      setOfflineQueue(queue);

      return new Response(JSON.stringify({ success: true, offline: true }), {
        status: 200,
        headers: { "Content-Type": "application/json" }
      });
    }

    throw error;
  }
};
// --- END OF OFFLINE-FIRST & CACHING INTERCEPTOR ---

interface StaffUser {
  email: string;
  role: string;
  name: string;
}

export default function App() {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<StaffUser | null>(null);
  const [routeHash, setRouteHash] = useState(window.location.hash);

  useEffect(() => {
    const handleHashChange = () => setRouteHash(window.location.hash);
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // Initialize authorization codes from local storage
  useEffect(() => {
    const cachedToken = localStorage.getItem("clinic_token");
    const cachedUser = localStorage.getItem("clinic_user");

    if (cachedToken && cachedUser) {
      try {
        setToken(cachedToken);
        setUser(JSON.parse(cachedUser));
      } catch (e) {
        console.error("Failed to restore session cache:", e);
        handleLogout();
      }
    }
  }, []);

  const handleLoginSuccess = (apiToken: string, activeUser: StaffUser) => {
    setToken(apiToken);
    setUser(activeUser);
  };

  const handleLogout = () => {
    localStorage.removeItem("clinic_token");
    localStorage.removeItem("clinic_user");
    setToken(null);
    setUser(null);
  };

  // Inactivity Auto-Logout (Clinical Security Standard)
  useEffect(() => {
    if (!token || !user) return; // Only track while logged in
    
    let inactivityTimer: NodeJS.Timeout;
    const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        handleLogout();
        // Option to add a toast or alert here, but simple logout is safer.
        console.warn("Session expired due to inactivity for security reasons.");
      }, INACTIVITY_LIMIT_MS);
    };

    // Attach listeners
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    
    // Initial start
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [token, user]);

  // Check if forcing public portal view (e.g. from QR code scan)
  const isForcePublic = window.location.search.includes('view=visitor');

  // Independent entry for the Dr. Wathiq Isolated Booking Page
  if (routeHash === "#booking-wathiq" || routeHash === "#dr-wathiq") {
    return <DrWathiqBookingPage />;
  }

  // Independent entry for the Queue Management TV Display
  if (routeHash === "#queue") {
    return <QueueDisplay />;
  }

  // Independent entry for Arabic Sticker Maker App Landing Page
  if (routeHash === "#arabic-sticker-maker") {
    return <StickerMakerLanding />;
  }

  // Independent entry for English Sticker Maker App Landing Page
  if (routeHash === "#sticker-maker-pro") {
    return <StickerMakerLandingEn />;
  }

  // Independent entry for Medical Calculators
  if (routeHash === "#calculators") {
    return <MedicalCalculators />;
  }

  // One-time patient link — validates token via Firestore before showing the form
  if (routeHash.startsWith("#patient-link=")) {
    const token = routeHash.split("=")[1];
    return <PatientLinkGate token={token} />;
  }

  // Independent entry for Patient Mode Calculators (legacy static links)
  if (routeHash.startsWith("#patient-form=")) {
    const calcId = routeHash.split("=")[1];
    return <MedicalCalculators defaultCalculator={calcId} isPatientMode={true} />;
  }

  // Independent entry for Reception Dashboard (for quick access from admin)
  if (routeHash === "#reception") {
    return <ReceptionDashboard user={user || { email: "reception@clinic.iq", name: "موظف الاستعلامات", role: "RECEPTIONIST" }} apiToken={token || ""} />;
  }

  // Independent entry for the financial and simplified accounting system
  if (routeHash === "#finance" || routeHash === "#finance-login" || routeHash === "#accounting") {
    return (
      <SuperAdminDashboard 
        user={user || { email: "finance@clinic.iq", name: "المشرف المالي", role: "FINANCE_OFFICER" }} 
        apiToken={token || ""} 
      />
    );
  }

  // Safe login guard
  if (!user || !token || isForcePublic) {
    try {
      // Clinical Security: Route explicitly to Staff Login if hash matches #login, #staff or #admin.
      // Otherwise, the default landing/start page is the Public Portal (Visitor page).
      if (!isForcePublic && (routeHash === "#login" || routeHash === "#staff" || routeHash === "#admin")) {
        const isSuperadminOnly = routeHash === "#admin";
        return <Login onLoginSuccess={handleLoginSuccess} isSuperadminOnly={isSuperadminOnly} />;
      } else {
        return <PublicPortal />;
      }
    } catch (routeError) {
      // Robust Error Handling for route dispatcher failures
      console.error("Clinical Router error: ", routeError);
      return <PublicPortal />;
    }
  }

  // Active dashboard resolving
  const currentRole = user.role;

  return (
    <PatientProvider>
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans select-none">
        <GoogleSheetBackgroundSync apiToken={token} />
        {/* Top Clinical Navigation Bar */}
        <header className="bg-white text-[#1E293B] border-b border-[#E2E8F0] shrink-0 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-transparent w-6 h-6 flex items-center justify-center rounded-full overflow-hidden">
                <IQDoctorLogo className="w-full h-full" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="font-bold text-[11px] tracking-tight text-[#0F172A] leading-none">IQ-doctor clinic</span>
                <span className="text-[7px] text-[#64748B] font-mono tracking-widest uppercase mt-0.5 leading-none">
                  Enterprise EHR Iraq v1.0
                </span>
              </div>
            </div>

            {/* User Meta Information */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <span className="text-[11px] font-bold text-[#1E293B] leading-none block">{user.name}</span>
                <span className="text-[8px] text-[#64748B] font-mono tracking-wide flex items-center gap-1 justify-end font-semibold mt-0.5 leading-none">
                  <UserCheck className="w-2.5 h-2.5 text-[#10B981]" />
                  Auth: {user.role}
                </span>
              </div>

              <button
                onClick={handleLogout}
                className="bg-[#F8FAFC] border border-[#E2E8F0] p-1 text-[#64748B] hover:text-[#0F172A] rounded-md hover:bg-slate-100 transition cursor-pointer select-none"
                title="Logout session"
              >
                <LogOut className="w-3 h-3" />
              </button>
            </div>
          </div>
        </header>

        <PatientBanner />

        {/* Main Panel Route Dispatcher (Protected RBAC Simulation layout) */}
        <main className="flex-1 overflow-y-auto max-w-7xl w-full mx-auto relative focus:outline-none" tabIndex={-1}>
          <ProtectedRoute
            currentRole={currentRole}
            user={user}
            token={token}
          />
        </main>

        {/* Footnote displaying Doctor's clinical administration details */}
        <footer className="w-full text-center py-3 px-4 border-t border-[#E2E8F0] bg-white shrink-0 select-text no-print">
          <p className="text-[11px] text-[#64748B] font-medium leading-relaxed flex items-center justify-center gap-2 flex-wrap">
            <span>© 2026 عيادة اي كيو دكتور (IQ-Doctor Clinic). جميع الحقوق محفوظة.</span>
            <span className="text-slate-300">|</span>
            <span>بادارة<span className="font-sans ml-1 text-slate-700 font-semibold">Dr. Watheq A. Hashim Al-Chalabi Reg. No. 17582 M.B.Ch.B, RCPI, Dip. Dermatology</span></span>
          </p>
        </footer>
      </div>
    </PatientProvider>
  );
}

interface ProtectedRouteProps {
  currentRole: string;
  user: StaffUser;
  token: string;
}

// Protected Route Module checking exact Employee Roles and compiling target views
function ProtectedRoute({ currentRole, user, token }: ProtectedRouteProps) {
  const targetRole = currentRole;

  switch (targetRole) {
    case "SUPER_ADMIN":
    case "PHYSICIAN":
      return (
        <ErrorBoundary>
          <PhysicianDashboard user={user} apiToken={token} />
        </ErrorBoundary>
      );

    case "RECEPTIONIST":
      return <ReceptionDashboard user={user} apiToken={token} />;

    case "NURSE":
      return <NursingDashboard user={user} apiToken={token} />;

    case "LAB_TECH":
      return <LabXrayDashboard user={user} apiToken={token} />;

    case "RADIOLOGY_TECH":
      return <LabXrayDashboard user={user} apiToken={token} />;

    case "PHARMACIST":
      return <PharmacyDashboard user={user} apiToken={token} />;

    default:
      return (
        <div className="p-12 text-center text-slate-600 bg-white border border-slate-150 rounded-3xl max-w-lg mx-auto my-12">
          <Shield className="w-12 h-12 text-rose-500 mx-auto mb-3 animate-pulse" />
          <h2 className="text-lg font-extrabold text-slate-800">RBAC Unauthorized State Block</h2>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">
            Your primary credentials role is "{user.role}". You do not have permissions to access the current dashboard module.
          </p>
        </div>
      );
  }
}
